<template>
  <figure class="viz-card">
    <figcaption class="viz-header">
      <h3>{{ title }}</h3>
      <span v-if="hiddenCount > 0" class="viz-note">+{{ hiddenCount }} more not shown</span>
    </figcaption>

    <ul v-if="legend.length" class="legend-row">
      <li v-for="entry in legend" :key="entry.label" class="legend-item">
        <span class="swatch" :style="{ background: toneColor(entry.tone) }"></span>
        <span class="legend-text">{{ entry.label }}</span>
      </li>
    </ul>

    <div v-if="loading" class="viz-empty">Loading…</div>
    <div v-else-if="!items.length" class="viz-empty">{{ emptyText }}</div>

    <!-- Always mounted (v-show, not v-if/v-else) so this is created exactly
         once and later data updates flow through ApexCharts' own reactive
         updateSeries path instead of a full destroy+recreate. Toggling it
         in/out of existence via v-if here caused many charts across every
         tab to mount at once (they all populate from the same batched API
         response) which raced with vue3-apexcharts' async mount and
         intermittently threw "Element not found". -->
    <apexchart
      v-show="!loading && items.length"
      type="bar"
      :height="chartHeight"
      :options="chartOptions"
      :series="chartSeries"
      class="apex-clickable"
      @dataPointSelection="onSelect"
    />
  </figure>
</template>

<script>
import { PALETTE, toneColor } from './chartPalette.js';

export default {
  name: 'BarChart',
  props: {
    title: { type: String, required: true },
    // [{ label, value, tone?, ...meta }], caller sorts descending. When any
    // item carries a `tone` ('good' | 'warning' | 'critical' | 'neutral' |
    // 'accent'), each bar is colored individually by its own tone instead of
    // the single `color` prop - e.g. per-trainee bars colored by pass/fail.
    items: { type: Array, default: () => [] },
    maxItems: { type: Number, default: 8 },
    emptyText: { type: String, default: 'No data available.' },
    loading: { type: Boolean, default: false },
    formatValue: { type: Function, default: null },
    color: { type: String, default: PALETTE.series1 },
    // [{ label, tone }] - an optional swatch legend shown under the title,
    // for explaining what each tone means when using per-item tone coloring.
    legend: { type: Array, default: () => [] },
    // Fixes the value axis's upper bound (e.g. 100 for a percentage chart)
    // instead of letting ApexCharts auto-scale it. Auto-scaling an all-zero
    // (or all-near-zero) series picks an arbitrary small max like 2, which
    // reads as a broken/empty chart rather than "genuinely 0%".
    maxValue: { type: Number, default: null },
  },
  emits: ['bar-click'],
  computed: {
    visibleItems() {
      return this.items.slice(0, this.maxItems);
    },
    hiddenCount() {
      return Math.max(0, this.items.length - this.maxItems);
    },
    hasPerItemTone() {
      return this.visibleItems.some((i) => i.tone);
    },
    chartHeight() {
      return Math.max(220, this.visibleItems.length * 40 + 60);
    },
    chartSeries() {
      return [{ name: this.title, data: this.visibleItems.map((i) => i.value) }];
    },
    chartOptions() {
      const format = this.format;
      return {
        chart: {
          toolbar: { show: false },
          animations: { easing: 'easeout', speed: 300 },
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, barHeight: '55%', distributed: this.hasPerItemTone, dataLabels: { position: 'center' } },
        },
        legend: { show: false }, // the swatch legend above covers this; avoid ApexCharts' own per-bar legend when distributed
        colors: this.hasPerItemTone
          ? this.visibleItems.map((i) => toneColor(i.tone || 'accent'))
          : [this.color],
        dataLabels: {
          enabled: true,
          formatter: (val) => format(val),
          style: { fontSize: '12px', fontWeight: 600, colors: ['#ffffff'] },
        },
        stroke: { width: 0 },
        xaxis: {
          categories: this.visibleItems.map((i) => i.label),
          labels: { style: { colors: PALETTE.textMuted, fontSize: '11px' } },
          // NB: despite `categories` living here, in horizontal mode this is
          // the axis ApexCharts scales numerically (min/max) - `yaxis` above
          // is the one rendering the category text on the left.
          min: 0,
          max: this.maxValue === null ? undefined : this.maxValue,
        },
        yaxis: {
          // In a horizontal bar chart ApexCharts renders the category names
          // (set via xaxis.categories above) along this axis - `maxWidth`
          // caps how much of the card a very long label (e.g. a task's full
          // description) can claim before it's truncated with an ellipsis,
          // so the bars themselves keep the rest of the width. Full text is
          // still available via the tooltip on hover.
          labels: { style: { colors: PALETTE.textSecondary, fontSize: '12.5px' }, maxWidth: 180 },
        },
        grid: { borderColor: PALETTE.gridline, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
        tooltip: {
          y: { formatter: (val) => format(val) },
        },
        states: {
          hover: { filter: { type: 'darken', value: 0.92 } },
          active: { filter: { type: 'darken', value: 0.85 } },
        },
      };
    },
  },
  methods: {
    format(value) {
      return this.formatValue ? this.formatValue(value) : value.toLocaleString();
    },
    toneColor,
    onSelect(event, chartContext, config) {
      const item = this.visibleItems[config.dataPointIndex];
      if (item) this.$emit('bar-click', item);
    },
  },
};
</script>

<style scoped>
.viz-card {
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 18px 20px 20px;
  margin: 0;
  flex: 1 1 360px;
  min-width: 0; /* let this shrink below its content's intrinsic width instead of forcing the row wider / wrapping a sibling out of view */
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
  transition: box-shadow 0.15s ease;
}

.viz-card:hover {
  box-shadow: 0 6px 16px rgba(11, 11, 11, 0.07);
}

.viz-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gridline);
}

.viz-header h3 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.viz-note {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.legend-row {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin: 0 0 14px;
  padding: 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-text {
  font-size: 12.5px;
  color: var(--text-secondary);
}

.viz-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 24px 0;
  text-align: center;
}

.apex-clickable :deep(.apexcharts-bar-area) {
  cursor: pointer;
}
</style>
